import { useEffect, useState } from "react";
import {
  createSpool,
  deleteSpool,
  fetchMaterials,
  listSpools,
  updateSpool,
  type Spool,
} from "../api";
import { Icon } from "../components/Icon";
import { Empty, Modal } from "../components/ui";
import { useToast } from "../lib/toast";
import type { MaterialProfile } from "../types";

import { useT } from "../i18n";
const PRESET_COLORS = ["#10b981", "#d94f4f", "#2f9e8f", "#3a3f4b", "#e0c341", "#9c6b3f", "#b8bcc4", "#6c5ce7"];

export function FilamentsPage() {
  const tr = useT();
  const [spools, setSpools] = useState<Spool[]>([]);
  const [materials, setMaterials] = useState<MaterialProfile[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ material: "PLA", vendor: "", color: "#10b981", total_g: 1000, cost_per_kg: 25, currency: "EUR" });
  const toast = useToast((s) => s.show);

  const load = () => listSpools().then(setSpools).catch((e) => toast(String(e.message), "bad"));
  useEffect(() => { load(); fetchMaterials().then(setMaterials).catch(() => {}); }, []);

  async function create() {
    try { await createSpool(form); setOpen(false); toast("Bobine ajoutée"); load(); }
    catch (e) { toast(String((e as Error).message), "bad"); }
  }
  async function act(fn: () => Promise<unknown>, ok: string, kind: "ok" | "bad" = "ok") {
    try { await fn(); toast(ok, kind); load(); } catch (e) { toast(String((e as Error).message), "bad"); }
  }

  const totalRemaining = spools.reduce((a, s) => a + s.remaining_g, 0);
  const totalValue = spools.reduce((a, s) => a + (s.remaining_g / 1000) * s.cost_per_kg, 0);
  const low = spools.filter((s) => s.low_stock).length;

  return (
    <div>
      <div className="page-head">
        <div>
          <div className="eyebrow" style={{ marginBottom: 8 }}>{tr("Système")}</div>
          <h1>{tr("Filaments")}</h1>
          <div className="sub">{tr("Inventaire des bobines : poids restant, coût, alertes stock bas. Déduit à chaque impression.")}</div>
        </div>
        <button className="btn primary" onClick={() => setOpen(true)}><Icon name="plus" size={15} /> Ajouter une bobine</button>
      </div>

      <div className="grid stats" style={{ marginBottom: 24 }}>
        <div className="card stat"><div className="ico"><Icon name="box" /></div><div className="v" style={{ fontSize: 26 }}>{spools.length}</div><div className="l">Bobines</div></div>
        <div className="card stat"><div className="ico"><Icon name="layers" /></div><div className="v" style={{ fontSize: 26 }}>{(totalRemaining / 1000).toFixed(2)}<span style={{ fontSize: 14, color: "var(--muted)" }}> kg</span></div><div className="l">Filament restant</div></div>
        <div className="card stat"><div className="ico"><Icon name="card" /></div><div className="v" style={{ fontSize: 26 }}>{totalValue.toFixed(0)}<span style={{ fontSize: 14, color: "var(--muted)" }}> €</span></div><div className="l">Valeur du stock</div></div>
        <div className="card stat"><div className="ico" style={low ? { color: "var(--warn)" } : undefined}><Icon name="alert" /></div><div className="v" style={{ fontSize: 26, color: low ? "var(--warn)" : undefined }}>{low}</div><div className="l">Stock bas</div></div>
      </div>

      {spools.length === 0 ? (
        <Empty icon="box">{tr("Aucune bobine. Ajoute ton inventaire de filament pour suivre conso et coûts.")}</Empty>
      ) : (
        <div className="grid cards" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }}>
          {spools.map((s) => {
            const pct = Math.round(s.remaining_pct * 100);
            const barColor = s.low_stock ? "var(--bad)" : pct < 40 ? "var(--warn)" : "var(--ok)";
            return (
              <div key={s.id} className="card">
                <div className="card-row" style={{ marginBottom: 10 }}>
                  <div className="row" style={{ gap: 11 }}>
                    <div style={{ width: 30, height: 30, borderRadius: 8, background: s.color, boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.14), 0 4px 12px ${s.color}55` }} />
                    <div>
                      <div className="card-title" style={{ fontSize: 14 }}>{s.material}{s.vendor ? ` · ${s.vendor}` : ""}</div>
                      <div className="muted num" style={{ fontSize: 11 }}>{s.cost_per_kg} {s.currency}/kg</div>
                    </div>
                  </div>
                  {s.low_stock && <span className="badge bad"><span className="dot" />Stock bas</span>}
                </div>
                <div className="row between" style={{ fontSize: 11.5, marginBottom: 5 }}>
                  <span className="muted">Restant</span>
                  <span className="num">{Math.round(s.remaining_g)} / {Math.round(s.total_g)} g · {pct}%</span>
                </div>
                <div className="progress" style={{ height: 7 }}><span style={{ width: `${pct}%`, background: barColor }} /></div>
                <div className="row" style={{ gap: 6, marginTop: 12, paddingTop: 11, borderTop: "1px solid var(--line)", justifyContent: "flex-end" }}>
                  <button className="btn sm ghost" title="Recharger (bobine pleine)" onClick={() => act(() => updateSpool(s.id, { remaining_g: s.total_g }), "Bobine rechargée")}><Icon name="zap" size={13} /> Recharger</button>
                  <button className="btn sm ghost" title={tr("Ajuster le restant")} onClick={() => { const g = prompt("Poids restant (g)", String(Math.round(s.remaining_g))); if (g != null && !Number.isNaN(Number(g))) act(() => updateSpool(s.id, { remaining_g: Number(g) }), "Mis à jour"); }}><Icon name="edit" size={13} /></button>
                  <button className="btn sm danger icon" title="Supprimer" onClick={() => act(() => deleteSpool(s.id), "Supprimé", "bad")}><Icon name="trash" size={13} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <Modal title="Ajouter une bobine" onClose={() => setOpen(false)}
          footer={<><button className="btn ghost" onClick={() => setOpen(false)}>Annuler</button><button className="btn primary" onClick={create}>Ajouter</button></>}>
          <div className="field"><label>{tr("Matière")}</label>
            <select value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })}>
              {(materials.length ? materials.map((m) => m.id) : ["PLA", "PETG", "ABS"]).map((id) => <option key={id} value={id}>{id}</option>)}
            </select>
          </div>
          <div className="field"><label>Fabricant</label><input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="Prusament, Bambu, …" /></div>
          <div className="field"><label>Couleur</label>
            <div className="row wrap" style={{ gap: 7 }}>
              {PRESET_COLORS.map((c) => (
                <button key={c} onClick={() => setForm({ ...form, color: c })} style={{ width: 26, height: 26, borderRadius: 7, background: c, cursor: "pointer", padding: 0, border: form.color === c ? "2px solid #fff" : "1px solid var(--line-2)" }} />
              ))}
              <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} style={{ width: 40, height: 28, padding: 2 }} />
            </div>
          </div>
          <div className="row" style={{ gap: 12 }}>
            <div className="field" style={{ flex: 1 }}><label>Poids (g)</label><input type="number" value={form.total_g} onChange={(e) => setForm({ ...form, total_g: Number(e.target.value) })} /></div>
            <div className="field" style={{ flex: 1 }}><label>Coût ({form.currency}/kg)</label><input type="number" value={form.cost_per_kg} onChange={(e) => setForm({ ...form, cost_per_kg: Number(e.target.value) })} /></div>
          </div>
        </Modal>
      )}
    </div>
  );
}
